/**
 * Memoria locale dell'invito a un torneo di chi deve ancora verificare l'email.
 *
 * Chi riceve `/?tournament=<id>&t=<gettone>` e non ha un account fa un giro
 * lungo: si registra, esce dall'app, apre la posta, clicca il link di verifica.
 * `register()` non autentica — chiede una verifica — e fra il primo click e il
 * ritorno nell'app non resta niente che dica dove la persona stava andando.
 * Senza questo ricordo l'invito si perde ESATTAMENTE nel momento in cui la
 * persona ha fatto quello che le avevamo chiesto.
 *
 * `localStorage`, non `sessionStorage`: il link di verifica lo apre quasi sempre
 * il client di posta, in un'altra finestra, e li' `sessionStorage` e' vuoto —
 * cioe' il ricordo sparirebbe proprio nel caso per cui esiste. E' la stessa
 * trappola gia' documentata in `pending-puzzle-link.ts`, e questo file ne
 * ricalca la forma apposta.
 *
 * Limite residuo, accettato come per il gemello: una webview dentro un'app e il
 * browser di sistema sono due magazzini separati, quindi un link aperto in uno e
 * verificato nell'altro si perde lo stesso — la persona ritocca il link.
 *
 * Si dimentica quando la pagina del torneo si apre con una sessione vera, e
 * all'uscita dall'account: su un dispositivo condiviso l'invito di un altro non
 * deve resuscitare.
 */

export const CHIAVE_INVITO_IN_SOSPESO = 'draughts-pending-tournament-invite';

/**
 * Ventiquattro ore: la stessa vita del gettone di verifica
 * (`getVerificationTokenExpiry`). Piu' in la' il giro della posta e' comunque
 * fallito, e riportare qualcuno a un torneo di ieri non e' un servizio.
 */
const ETA_MASSIMA_MS = 24 * 60 * 60 * 1000;

export interface PendingTournamentInvite {
  id: string;
  /** Il gettone c'e' solo per i tornei privati: per i pubblici e' `null`. */
  token: string | null;
  ts: number;
}

/**
 * L'invito in sospeso, o `null`. Si autopulisce di qualunque record inservibile
 * — JSON rotto, identificativo mancante, troppo vecchio — cosi' un record
 * guasto non resta a intralciare i giri successivi.
 */
export function leggiInvitoInSospeso(): PendingTournamentInvite | null {
  let grezzo: string | null;
  try {
    grezzo = localStorage.getItem(CHIAVE_INVITO_IN_SOSPESO);
  } catch {
    // Navigazione privata o memoria negata: l'invito non si riprende, e basta.
    return null;
  }
  if (!grezzo) return null;

  try {
    const letto = JSON.parse(grezzo) as Partial<PendingTournamentInvite>;
    const { id, token, ts } = letto;
    if (
      typeof id !== 'string' ||
      id.length === 0 ||
      id.length > 64 ||
      (token !== null && typeof token !== 'string') ||
      typeof ts !== 'number' ||
      Date.now() - ts > ETA_MASSIMA_MS
    ) {
      dimenticaInvitoInSospeso();
      return null;
    }
    return { id, token: token ?? null, ts };
  } catch {
    dimenticaInvitoInSospeso();
    return null;
  }
}

/**
 * Mette da parte l'invito. **Non declassa un gettone**: chiamata con `null`
 * mentre per lo stesso torneo ce n'e' uno in memoria, quello resta.
 *
 * Un gettone nullo non e' una notizia su un gettone. Da quando la pagina di un
 * torneo ha un indirizzo suo — `/?tournament=<id>`, senza la chiave — un
 * ricarico ripassa da qui con `token: null`, e una scrittura incondizionata
 * cancellerebbe la chiave arrivata col link privato. Il guasto sarebbe muto e
 * nel punto peggiore: la persona finisce il giro della posta, torna al torneo
 * **senza chiave**, e le si chiede una password che nessuno le ha dato.
 *
 * `ts` si rinfresca comunque: chi sta ricaricando quella pagina adesso ha
 * ancora quell'invito per le mani, e le ventiquattr'ore contano da adesso.
 */
export function salvaInvitoInSospeso(id: string, token: string | null): void {
  try {
    const precedente = leggiInvitoInSospeso();
    const gettone = token ?? (precedente?.id === id ? precedente.token : null);
    const carico: PendingTournamentInvite = { id, token: gettone, ts: Date.now() };
    localStorage.setItem(CHIAVE_INVITO_IN_SOSPESO, JSON.stringify(carico));
  } catch {
    // Memoria negata: il giro della posta non si riprendera', e non e' un errore
    // da mostrare a nessuno.
  }
}

export function dimenticaInvitoInSospeso(): void {
  try {
    localStorage.removeItem(CHIAVE_INVITO_IN_SOSPESO);
  } catch {
    // Niente da fare.
  }
}

/** Che fare del ricordo, adesso. Vedi `decidiRitornoAlTorneo`. */
export type EsitoRitorno = 'aspetta' | 'dimentica' | 'torna';

/**
 * Chi torna dall'invito, e chi invece non va dirottato da nessuna parte.
 *
 * La regola sta qui, e non dentro un effetto di `App.tsx`, perche' e' l'unica
 * cosa di tutto il giro che si puo' sbagliare in silenzio: dirottare qualcuno
 * che non ha chiesto niente non da' errori, da' solo una persona che si ritrova
 * in un torneo mentre voleva giocare.
 *
 * Le tre risposte:
 *
 *  - **torna** — chi ha gia' un conto e ACCEDE durante questa visita: e' esatta-
 *    mente la persona che aveva aperto l'invito, l'ha aperto ora, e il login era
 *    solo il pedaggio. Si riporta al torneo, col gettone del link.
 *  - **dimentica** — chi e' gia' dentro al torneo (non c'e' niente da riprendere)
 *    e chi apre l'app con la sessione GIA' aperta: quella e' una visita nuova, e
 *    un invito di ieri che si prende lo schermo e' la trappola che il gemello dei
 *    puzzle documenta («un link ripescato dalla memoria non prende mai lo
 *    schermo, arma solo il ritorno»).
 *  - **aspetta** — chi non e' ancora entrato, ospiti compresi: il ricordo serve
 *    ancora, ed e' tutto il suo mestiere sopravvivere al giro della posta.
 *
 * Chi NON ha un conto non compare qui: si registra, e il ritorno lo fa la
 * schermata di verifica (`VerifyEmailPage`), che e' l'altro capo dello stesso
 * viaggio.
 */
export function decidiRitornoAlTorneo(stato: {
  autenticato: boolean;
  ospite: boolean;
  /** Se in questa visita la persona e' gia' stata vista fuori (sloggata o ospite). */
  eraFuori: boolean;
  invito: PendingTournamentInvite | null;
  giaSulTorneo: boolean;
}): EsitoRitorno {
  if (!stato.invito) return 'aspetta';
  if (!stato.autenticato || stato.ospite) return 'aspetta';
  if (stato.giaSulTorneo) return 'dimentica';
  if (!stato.eraFuori) return 'dimentica';
  return 'torna';
}
